import { useState } from "react"
import { RxCrossCircled } from "react-icons/rx"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import EmailIcon from "../../../Utils/assets/img/icons/Emaillogo.png"
import smsIcon from "../../../Utils/assets/img/icons/SMS.png"
import bravo from "../../../Utils/assets/img/Icons1/bravo.png";
import "./Integrations.css"
import { BsArrowLeft } from "react-icons/bs"
import { Button } from "react-bootstrap"
import { GetRequest, PutRequest, PostRequest } from "../../../Utils/hooks/ApiHandler"
import useLocalStorageState from "use-local-storage-state"
import { BsEye, BsEyeSlash } from "react-icons/bs"
import { toast } from "sonner"
import { useGetEssentials } from "../../../Utils/hooks/useGetEssentials"

// Hardcoded SMTP values
const SMTP_DEFAULTS = {
  smtpType: "smtp",
  smtpApi: "123456",
  smtpHost: "ssl://smtp.gmail.com",
}

// Schemas
const smtpSchema = z.object({
  smtpPort: z
    .string()
    .min(1, "SMTP Port is required"),
  smtpUser: z.string().min(1, "SMTP User is required").email("Must be a valid email"),
  smtpPassword: z.string().min(1, "SMTP Password is required"),
})

const brevoSchema = z.object({
  wApiKey: z.string().min(1, "API Key is required"),
  brevoSenderName: z.string().min(1, "Sender Name is required"),
  brevoEmail: z.string().min(1, "Sender Email is required").email("Must be a valid email"),
})

const senditSchema = z.object({
  wApiKey: z.string().min(1, "API Key is required"),
  senderEmail: z.string().min(1, "Sender Email is required").email("Must be a valid email"),
})

export const EmailIntegration = () => {
  const [selectedService, setSelectedService] = useState(null)
  const [existingData, setExistingData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  // getting essentials for api fetching
  const { customer, setCustomer, headers, extras, navigate, customerId, loginUserId, userType } = useGetEssentials()
  const [PERMISSION, _] = useLocalStorageState('permission')

  // integration permission
  const integrationPermission = PERMISSION.setting.feature.integration;

  const emailServices = [
    {
      name: "SMTP",
      description: "Use this to setup email SMTP to send notifications, forms, and receipts.",
      icon: smsIcon,
      schema: smtpSchema,
      endpoint: "fetchSmtpDetails",
      type: "smtp",
      updateEndpoint: "updateSmtpEmail",
      addEndpoint: "AddSmtpEmail",
      fields: ["smtpPort", "smtpUser", "smtpPassword"],
    },
    {
      name: "Brevo",
      description: "Use your Brevo API key to send email messages.",
      icon: bravo,
      schema: brevoSchema,
      endpoint: "fetchSmtpDetails",
      type: "brevo",
      updateEndpoint: "updateBrevo",
      addEndpoint: "insertBrevo",
      fields: ["wApiKey", "senderNumber"],
    },
    {
      name: "Sendit",
      description: "Use your Sendit API key to send email messages.",
      icon: EmailIcon,
      schema: senditSchema,
      endpoint: "fetchSenditKey",
      updateEndpoint: "updateSenditKey",
      addEndpoint: "addSenditKey",
      type: "sendit",
      fields: ["wApiKey", "senderEmail"],
    },
  ]

  // Get current schema based on selected service
  const getCurrentSchema = () => {
    if (!selectedService) return smtpSchema
    return selectedService.schema
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(getCurrentSchema()),
  })



  const fetchServiceData = async (service) => {
    setIsLoading(true)
    try {
      const res = await GetRequest(
        `/integration/${service.endpoint}/${customerId}/${service.type}`,
        { headers },
        extras,
      )


      if (res.status === "success" && res.data) {

        setExistingData(res.data)

        // Populate form with existing data based on service type
        if (service.name === "SMTP") {

          const data = res.data[0]
          // Only populate editable fields
          if (data.smtp_port) setValue("smtpPort", data.smtp_port.toString())
          if (data.smtp_user) setValue("smtpUser", data.smtp_user)
          if (data.smtp_password) setValue("smtpPassword", data.smtp_password)
        } else if (service.name === "Brevo") {

          const data = res.data[0]
          if (data.api_key) {

            setValue("wApiKey", data.api_key)
          }
          if (data.sender_name) setValue("brevoSenderName", data.sender_name)
          if (data.smtp_user) setValue("brevoEmail", data.smtp_user)
        } else if (service.name === "Sendit") {

          const data = res.data[0]
          if (data.wApiKey) setValue("wApiKey", data.wApiKey)
          if (data.senderEmail) setValue("senderEmail", data.senderEmail)
        }
        return { hasData: true, data: res.data }
      } else {

        setExistingData(null)
        reset() // Clear form
        return { hasData: false, data: null }
      }
    } catch (err) {
      console.error("Error fetching service data:", err)
      setExistingData(null)
      reset()
      return { hasData: false, data: null }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCardClick = async (service) => {
    setSelectedService(service)
    // Reset form with new schema
    reset()
    await fetchServiceData(service)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const handleCloseModal = () => {
    setSelectedService(null)
    setExistingData(null)
    setShowPassword(false) // Reset password visibility
    reset()
  }

  const onSubmit = async (data) => {


    if (!selectedService) return

    setIsLoading(true)
    try {
      let res
      if (existingData) {
        // Update existing data

        let updatePayload = {}
        let updateEndpoint = ""

        if (selectedService.name === "SMTP") {
          updatePayload = {
            // Always use hardcoded values
            type: SMTP_DEFAULTS.smtpType,
            api: SMTP_DEFAULTS.smtpApi,
            host: SMTP_DEFAULTS.smtpHost,
            // Use user input for these fields
            port: Number.parseInt(data.smtpPort),
            user: data.smtpUser,
            password: data.smtpPassword,
            smtp_id: existingData[0]?.id

          }
          updateEndpoint = `/integration/${selectedService.updateEndpoint}`
        } else if (selectedService.name === "Brevo") {
          updatePayload = {
            api_key: data.wApiKey,
            senderEmail: data.brevoEmail,
            senderName: data.brevoSenderName,

            smtp_id: existingData[0]?.id
          }




          updateEndpoint = `/integration/${selectedService.updateEndpoint}`
        } else if (selectedService.name === "Sendit") {
          updatePayload = {
            wApiKey: data.wApiKey,
            senderEmail: data.senderEmail,
            customer_id: customerId,
          }
          updateEndpoint = `/integration/${selectedService.updateEndpoint}/${customerId}/${existingData[0]?.id}`
        }

        res = await PutRequest(updateEndpoint, updatePayload, { headers }, extras)

      } else {

        let addPayload = {}
        let addEndpoint = ""

        if (selectedService.name === "SMTP") {
          addPayload = {
            type: SMTP_DEFAULTS.smtpType,
            api: SMTP_DEFAULTS.smtpApi,
            port: Number.parseInt(data.smtpPort),
            host: SMTP_DEFAULTS.smtpHost,
            user: data.smtpUser,
            password: data.smtpPassword,
            customer_id: customerId,
            user_login_id: loginUserId,
          }
          addEndpoint = `/integration/${selectedService.addEndpoint}`
        } else if (selectedService.name === "Brevo") {
          addPayload = {
            api_key: data.wApiKey,
            senderName: data.brevoSenderName,
            senderEmail: data.brevoEmail,
            type: "brevo",
            customer_id: customerId,
            user_login_id: loginUserId
          }

          addEndpoint = `/integration/${selectedService.addEndpoint}/${customerId}`
        } else if (selectedService.name === "Sendit") {
          addPayload = {
            wApiKey: data.wApiKey,
            senderEmail: data.senderEmail,
            type: "sendit",
            customer_id: customerId,
          }
          addEndpoint = `/integration/${selectedService.addEndpoint}/${customerId}`
        }


        res = await PostRequest(addEndpoint, addPayload, false, { headers }, extras)

      }


      if (res.status === "success") {
        toast.success(`${selectedService.name} configuration ${existingData ? "updated" : "saved"} successfully!`)
        handleCloseModal()
      } else {
        toast.error(`Failed to ${existingData ? "update" : "save"} ${selectedService.name} configuration`)
      }
    } catch (error) {
      console.error("Error submitting form:", error)
      toast.error(`Error ${existingData ? "updating" : "saving"} ${selectedService.name} configuration`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="mb-5" style={{ marginLeft: "6px" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <div className="d-flex flex-column text-start">
            <h2 className="h4 mb-2">Email Integration</h2>
            <span className="text-muted mb-1">Manage your email API integrations from here.</span>
          </div>
          <Button variant="outline-primary" onClick={() => navigate(-1)}>
            <BsArrowLeft className="me-1" /> Back
          </Button>
        </div>
        <hr className="text-muted" />
        <div className="row gx-3 gy-3 justify-content-start ms-1">
          {emailServices.map((item, index) => (
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 d-flex" key={index} style={{ maxWidth: "260px" }}>
              <div
                className="card text-center border-0 shadow-sm custom-card-integration w-100"
                onClick={() => {
                  if (!integrationPermission?.isCreate) {
                    toast.warning("You are not authorized for this action!")
                    return
                  }
                  handleCardClick(item)
                }}
                style={{
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  opacity: isLoading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
              >
                <div className="card-body p-4 d-flex flex-column align-items-center">
                  <img
                    src={item.icon || "/placeholder.svg"}
                    alt={item.name}
                    className="card-logo-integration mb-3"
                    style={{ height: "32px" }}
                  />
                  <h6 className="text-truncate w-100 small fw-semibold mb-2">{item.name}</h6>
                  <div className="tooltip-integration w-100">
                    <p className="card-description text-muted small m-0 text-truncate-2">{item.description}</p>
                    <div className="tooltip-box-integration">{item.description}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SMTP Modal */}
      {selectedService?.name === "SMTP" && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={handleCloseModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content p-3">
              <div className="modal-header border-bottom pb-2">
                <div className="d-flex align-items-start justify-content-between w-100">
                  <div className="d-flex">
                    <img
                      src={selectedService.icon || "/placeholder.svg"}
                      alt={selectedService.name}
                      style={{ height: "32px", width: "32px", marginRight: "12px" }}
                    />
                    <div>
                      <h5 className="modal-title mb-0">
                        {existingData ? "Update Email SMTP Setting" : "Email SMTP Setting"}
                      </h5>
                      <small className="text-muted">
                        Use this setting to setup email SMTP to send notifications, updates, admission forms and
                        receipts.
                      </small>

                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleCloseModal}
                    aria-label="Close"
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.8rem",
                      cursor: "pointer",
                      color: "#888",
                      marginTop: "-20px",
                      opacity: "0.4",
                    }}
                  >
                    <RxCrossCircled />
                  </button>
                </div>
              </div>
              <div className="modal-body pt-3">
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row g-3">
                      {/* Hardcoded SMTP Type - Non-editable */}
                      <div className="col-md-6">
                        <label className="form-label">
                          SMTP Type <span className="text-muted">(Default)</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={SMTP_DEFAULTS.smtpType}
                          disabled
                          style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                        />

                      </div>

                      {/* Hardcoded SMTP API - Non-editable */}
                      <div className="col-md-6">
                        <label className="form-label">
                          SMTP API <span className="text-muted">(Default)</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={SMTP_DEFAULTS.smtpApi}
                          disabled
                          style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                        />

                      </div>

                      {/* SMTP Port - Editable but restricted to 465 or 466 */}
                      <div className="col-md-6">
                        <label className="form-label">
                          SMTP Port <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          {...register("smtpPort")}
                          className={`form-control ${errors.smtpPort ? "is-invalid" : ""}`}
                          placeholder="465/587"
                        />

                        {errors.smtpPort && <div className="invalid-feedback">{errors.smtpPort.message}</div>}

                      </div>

                      {/* Hardcoded HOST - Non-editable */}
                      <div className="col-md-6">
                        <label className="form-label">
                          HOST <span className="text-muted">(Default)</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={SMTP_DEFAULTS.smtpHost}
                          disabled
                          style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                        />

                      </div>

                      {/* Editable SMTP User field */}
                      <div className="col-md-6">
                        <label className="form-label">
                          SMTP User <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          {...register("smtpUser")}
                          className={`form-control ${errors.smtpUser ? "is-invalid" : ""}`}
                          placeholder="abc@gmail.com"
                        />
                        {errors.smtpUser && <div className="invalid-feedback">{errors.smtpUser.message}</div>}
                      </div>

                      {/* Editable SMTP Password field with eye toggle */}
                      <div className="col-md-6">
                        <label className="form-label">
                          SMTP Password <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            {...register("smtpPassword")}
                            className={`form-control ${errors.smtpPassword ? "is-invalid" : ""}`}
                            placeholder="App Password"
                            style={{ paddingRight: "40px" }}
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute"
                            onClick={togglePasswordVisibility}
                            style={{
                              right: "8px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              border: "none",
                              background: "none",
                              padding: "0",
                              color: "#6c757d",
                              zIndex: 10,
                            }}
                            tabIndex={-1}
                          >
                            {showPassword ? <BsEyeSlash size={16} /> : <BsEye size={16} />}
                          </button>
                        </div>
                        {errors.smtpPassword && <div className="invalid-feedback">{errors.smtpPassword.message}</div>}
                      </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCloseModal}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn"
                        style={{ backgroundColor: "rgb(122,58,94)", color: "white" }}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            {existingData ? "Updating..." : "Saving..."}
                          </>
                        ) : (
                          "Save"
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Brevo Modal */}
      {selectedService?.name === "Brevo" && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={handleCloseModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content p-3">
              <div className="modal-header border-bottom pb-2">
                <div className="d-flex align-items-start justify-content-between w-100">
                  <div className="d-flex">
                    <img
                      src={selectedService.icon || "/placeholder.svg"}
                      alt={selectedService.name}
                      style={{ height: "32px", width: "32px", marginRight: "12px" }}
                    />
                    <div>
                      <h5 className="modal-title mb-0">
                        {existingData ? "Update Brevo API Setting" : "Brevo API Setting"}
                      </h5>
                      <small className="text-muted">Configure Brevo API to send email messages.</small>

                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleCloseModal}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.8rem",
                      cursor: "pointer",
                      color: "#888",
                      marginTop: "-20px",
                      opacity: "0.4",
                    }}
                  >
                    <RxCrossCircled />
                  </button>
                </div>
              </div>
              <div className="modal-body pt-3">
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-3">
                      <label className="form-label">
                        Brevo API Key <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        {...register("wApiKey")}
                        className={`form-control ${errors.wApiKey ? "is-invalid" : ""}`}
                        placeholder="Enter your API key"
                      />
                      {errors.wApiKey && <div className="invalid-feedback">{errors.wApiKey.message}</div>}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Brevo Sender Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        {...register("brevoSenderName")}
                        className={`form-control ${errors.brevoSenderName ? "is-invalid" : ""}`}
                        placeholder="Enter your API key"
                      />
                      {errors.brevoSenderName && <div className="invalid-feedback">{errors.brevoSenderName.message}</div>}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Sender Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        {...register("brevoEmail")}
                        className={`form-control ${errors.brevoEmail ? "is-invalid" : ""}`}
                        placeholder="example@email.com"
                      />
                      {errors.brevoEmail && <div className="invalid-feedback">{errors.brevoEmail.message}</div>}
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCloseModal}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn"
                        style={{ backgroundColor: "rgb(122,58,94)", color: "white" }}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            {existingData ? "Updating..." : "Saving..."}
                          </>
                        ) : (
                          "Save"
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sendit Modal */}
      {selectedService?.name === "Sendit" && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={handleCloseModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            {" "}
            <div className="modal-content p-3">
              <div className="modal-header border-bottom pb-2">
                <div className="d-flex align-items-start justify-content-between w-100">
                  <div className="d-flex">
                    <img
                      src={selectedService.icon || "/placeholder.svg"}
                      alt={selectedService.name}
                      style={{ height: "32px", width: "32px", marginRight: "12px" }}
                    />
                    <div>
                      <h5 className="modal-title mb-0">
                        {existingData ? "Update Sendit API Setting" : "Sendit API Setting"}
                      </h5>
                      <small className="text-muted">Enter Sendit API credentials to send email messages.</small>

                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleCloseModal}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.8rem",
                      cursor: "pointer",
                      color: "#888",
                      marginTop: "-20px",
                      opacity: "0.4",
                    }}
                  >
                    <RxCrossCircled />
                  </button>
                </div>
              </div>
              <div className="modal-body pt-3">
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-3">
                      <label className="form-label">
                        API Key <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        {...register("wApiKey")}
                        className={`form-control ${errors.wApiKey ? "is-invalid" : ""}`}
                        placeholder="Enter API key"
                      />
                      {errors.wApiKey && <div className="invalid-feedback">{errors.wApiKey.message}</div>}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Sender Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        {...register("senderEmail")}
                        className={`form-control ${errors.senderEmail ? "is-invalid" : ""}`}
                        placeholder="example@email.com"
                      />
                      {errors.senderEmail && <div className="invalid-feedback">{errors.senderEmail.message}</div>}
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCloseModal}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn"
                        style={{ backgroundColor: "rgb(122,58,94)", color: "white" }}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            {existingData ? "Updating..." : "Saving..."}
                          </>
                        ) : (
                          "Save"
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
